import React, { useState } from 'react';
import { Text } from 'atoms/Text/Text';
import { Styles } from './styles';
import CloseIcon from 'assets/svgs/closeIcon.svg';
import { useModal } from 'contexts/ModalProvider/ModalProvider';
import { AlgoliaProduct } from 'models/products';
import { CustomEventName, logTrackingEvent } from 'services/analytics';
import { Button } from 'atoms/Button/Button';
import { Checkboxes } from 'atoms/Checkboxes/Checkboxes';
import { TextArea } from 'atoms/TextArea/TextArea';
import { Spacer } from 'components/Spacer/Spacer';
import { TEST_IDS } from 'constants/testIds';
import { defaultTheme } from 'constants/theme/defaultTheme';
import Tick from 'assets/svgs/tick.svg';
import { Input } from 'atoms/Input/Input';

type ReportProblemModalProps = {
  product: AlgoliaProduct;
};

export const ReportProblemModal: React.FC<ReportProblemModalProps> = ({ product }) => {
  const [options, setOptions] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { handleModal } = useModal();

  const onCheck = (value: string) => {
    let updatedOptions = [...options, value];
    if (options.includes(value)) {
      updatedOptions = options.filter(i => i !== value);
    }
    setOptions(updatedOptions);
  };

  const onSubmit = async () => {
    await logTrackingEvent({
      name: CustomEventName.ReportProductProblem,
      data: {
        skuName: product.skuName,
        description,
        productName: product.productName,
        missingInfo: options.includes('missing').toString(),
        incorrectInfo: options.includes('incorrect').toString(),
        otherInfo: options.includes('other').toString(),
        email,
      },
    });
    setIsSubmitted(true);
  };

  const onConfirm = () => {
    setIsSubmitted(false);
    handleModal();
  };

  if (isSubmitted) {
    return (
      <Styles.Wrapper>
        <Styles.ConfirmContent>
          <Styles.TickWrapper>
            <Tick />
          </Styles.TickWrapper>
          <Spacer.Column numberOfSpaces={5} />
          <Text.h2 center>Your issue has been submitted successfully</Text.h2>
          <Spacer.Column numberOfSpaces={2} />
          <Text.body3 center>We will investigate and contact you with an update</Text.body3>
        </Styles.ConfirmContent>
        <Styles.Button>
          <Button
            text="Okay"
            large
            block
            onClick={onConfirm}
            testID={TEST_IDS.reportProblem.okay}
          />
        </Styles.Button>
      </Styles.Wrapper>
    );
  }

  return (
    <Styles.Wrapper>
      <Styles.Heading>
        <div />
        <Text.button3>Report a product issue</Text.button3>
        <Styles.Close onClick={handleModal}>
          <CloseIcon />
        </Styles.Close>
      </Styles.Heading>
      <Styles.Content>
        <Text.button2>Product</Text.button2>
        <Text.button2>
          {product.productName} | {product.companyName}
        </Text.button2>
        <Checkboxes
          options={[
            { label: 'Missing information', value: 'missing' },
            { label: 'Incorrect information', value: 'incorrect' },
            { label: 'Other issue', value: 'other' },
          ]}
          selected={options}
          initialColumns={1}
          handleChange={onCheck}
          label="Issue"
        />
        <Spacer.Column numberOfSpaces={4} />
        <TextArea
          label="Description"
          onChange={e => setDescription(e.target.value)}
          name="Description"
          placeholder="Describe the issue here..."
          value={description}
          maxChars={500}
          testId={TEST_IDS.reportProblem.textArea}
        />
        <Styles.Guidance float="left">
          <Text.caption2 color={defaultTheme.colors.secondaryGrey}>Required</Text.caption2>
        </Styles.Guidance>
        <Styles.Guidance float="right">
          <Text.caption2 color={defaultTheme.colors.secondaryGrey}>
            {description.length}/500
          </Text.caption2>
        </Styles.Guidance>
        <Spacer.Column numberOfSpaces={8} />
        <Text.button2>Email address</Text.button2>
        <Input value={email} onChange={e => setEmail(e.target.value)} />
      </Styles.Content>
      <Styles.Button>
        <Button
          text="Submit"
          large
          block
          onClick={onSubmit}
          disabled={!options.length || !description}
          testID={TEST_IDS.reportProblem.submit}
        />
      </Styles.Button>
    </Styles.Wrapper>
  );
};
